import { useEffect, useMemo, useState } from 'react';
import {
    Device,
    DeviceConsumptionType,
    SupplierInfo,
    RawSuppliersList,
} from 'types';
import { DeviceConfigInfo } from './sections/DeviceConfigInfo/DeviceConfigInfo';
import { useAuth } from 'hooks/useAuth';
import { getSupplierInfo } from 'store/services/supplier';
import * as yup from 'yup';
import { FormProvider, FormSubmitButton } from 'components/form';
import { ConfirmationDialog } from 'components/modals/ConfirmationDialog/ConfirmationDialog';
import { DeviceConfigTank } from './sections/DeviceConfigTank/DeviceConfigTank';
import { DeviceConfigNotes } from './sections/DeviceConfigNotes/DeviceConfigNotes';
import {
    useAdminPatchDeviceStatusMutation,
    useAdminPatchDeviceSupplierIdMutation,
    useAdminRemoveSupplierFromDeviceMutation,
    useLazyPatchUpdateDeviceQuery,
} from 'store/services';
import _ from 'lodash';
import { activateSnackbar } from 'store/features/ui';
import { FieldValues } from 'react-hook-form';
import { useAppDispatch } from 'store';
import { DeviceConfigBackofficeSync } from './sections/DeviceConfigBackofficeSync/DeviceConfigBackofficeSync';
import {
    getBackOfficeAccountNumber,
    getBackOfficeTankNumber,
} from 'store/services/third-party-backoffice';
import {
    getObjectDiff,
    recursivelyNullifyUndefinedValues,
} from 'helpers/general';
import styles from './DeviceConfig.module.scss';
import { DeviceConfigAdmin } from './sections/DeviceConfigAdmin/DeviceConfigAdmin';
import { getUserProtected } from 'lib/firebase';
import { FirebaseUserProtected } from 'types/User/firebaseTypes';
import { getSupplierIdsToNamesMap } from 'store/services/suppliers';
import { useSelector } from 'react-redux';
import { useViewAsSupplierSelector } from 'store/features/viewAsSupplier';
import { BackofficeDeliveryModal } from './sections/DeviceConfigBackofficeSync/modals/BackofficeDeliveryModal/BackofficeDeliveryModal';
import Save from '@mui/icons-material/Save';
import { getGeocodeData } from 'helpers/device';

export type DeviceConfigInput = {
    consumptionType: (keyof DeviceConsumptionType)[] | string[];
    backOfficeAccountNumber: string;
    backOfficeTankNumber: string;
    deviceSupplierId?: string;
    deviceSupplierName?: string;
    status?: string;
} & Pick<
    Device,
    | 'name'
    | 'customer_name'
    | 'group'
    | 'address'
    | 'delivery_type'
    | 'capacity'
    | 'orientation'
    | 'delivery_instructions'
    | 'gaugeType'
    | 'fuelType'
    | 'notes'
    | 'accountId'
    | 'billing'
    | 'equation_id'
>;

const deviceConfigSchema = yup.object<{
    [k in keyof DeviceConfigInput]: yup.AnySchema;
}>({
    // Info Section
    customer_name: yup.string(),
    name: yup.string(),
    address: yup
        .string()
        .test(
            'valid-geocode',
            'There was a problem getting the geocode for this address',
            async function (value) {
                if (!value) {
                    return true;
                }

                const { error } = await getGeocodeData(value);

                if (error) {
                    return this.createError({
                        message: error,
                    });
                }

                return true;
            }
        ),
    delivery_type: yup.string(),
    group: yup.string(),
    billing: yup.object<{
        [k in keyof DeviceConfigInput['billing']]: yup.AnySchema;
    }>({
        pricePerGallon: yup.number(),
        taxPercentage: yup.number(),
    }),
    // Tank Section
    fuelType: yup.string(),
    gaugeType: yup.string().when('fuelType', {
        is: (fuelType: string) => fuelType === 'oil',
        then: (schema) => schema.required(),
    }),
    capacity: yup.number().moreThan(0, 'Capacity must be greater than 0'),
    orientation: yup.string(),
    // NOTE we have to convert this array into an object on form submit
    consumptionType: yup.array(),
    delivery_instructions: yup.string(),

    // Notes Section
    notes: yup.string(),

    // Backoffice Sync Section
    accountId: yup.string(),
    backOfficeAccountNumber: yup.string(),
    backOfficeTankNumber: yup.string(),

    // Admin Section
    equation_id: yup.string(),
    deviceSupplierId: yup.string(),
    status: yup.string(),
});

export interface DeviceConfigProps {
    /** Device to configure. */
    device: Device;
    /** Callback to alert parent component about device updates. */
    setDevice: React.Dispatch<React.SetStateAction<Device | undefined>>;
}
export const DeviceConfig = ({ device, setDevice }: DeviceConfigProps) => {
    const { user } = useAuth();
    const dispatch = useAppDispatch();
    const { viewAsSupplier } = useSelector(useViewAsSupplierSelector);

    const [supplierInfo, setSupplierInfo] = useState<SupplierInfo>();
    const [deviceInput, setDeviceInput] = useState<DeviceConfigInput | null>(
        null
    );
    const [userProtected, setUserProtected] = useState<FirebaseUserProtected>();
    const [supplierList, setSupplierList] = useState<RawSuppliersList>();
    const [supplierName, setSupplierName] = useState<string>('');
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [updateDeviceQuery] = useLazyPatchUpdateDeviceQuery();
    const [removeSupplierFromDeviceQuery] =
        useAdminRemoveSupplierFromDeviceMutation();
    const [updateDeviceSupplierIdQuery] =
        useAdminPatchDeviceSupplierIdMutation();
    const [updateDeviceStatusQuery] = useAdminPatchDeviceStatusMutation();

    // Create Backoffice Delivery (Cargas only)
    const [backofficeDeliveryDialogOpen, setBackofficeDeliveryDialogOpen] =
        useState(false);
    const [tankNumber, setTankNumber] = useState('');

    // Get supplier info.
    useEffect(() => {
        if (viewAsSupplier) {
            getSupplierInfo(viewAsSupplier).then(
                (supplierInfo: SupplierInfo) => {
                    if (supplierInfo) setSupplierInfo(supplierInfo);
                }
            );
        }

        // Only the admin user needs user protected data for the Admin section.
        if (user?.protected?.supplierId?.toString() === '0' && user?.uid) {
            getUserProtected(user?.uid).then(
                (userProtected: FirebaseUserProtected | null) => {
                    if (userProtected) setUserProtected(userProtected);
                }
            );
        }

        // Only the admin user needs supplier list data for the Admin section.
        if (user?.protected?.supplierId?.toString() === '0') {
            getSupplierIdsToNamesMap().then((data: RawSuppliersList) => {
                if (data) {
                    setSupplierList(data);
                }
            });
        }
    }, [user?.protected, user?.uid, viewAsSupplier]);

    // Set tank number for creating cargas delivery.
    useEffect(() => {
        setTankNumber(getBackOfficeTankNumber(device.accountId));
    }, [device]);

    // Get supplier name from supplierId on device.
    useEffect(() => {
        // Only need this for admin users.
        if (
            user?.protected?.supplierId?.toString() === '0' &&
            user?.uid &&
            device.protected?.supplierId
        ) {
            getSupplierInfo(device.protected.supplierId).then(
                (info: SupplierInfo) => {
                    setSupplierName(info.name);
                }
            );
        }
    }, [device, user?.protected, user?.uid]);

    const defaultDeviceConfigValues: DeviceConfigInput = useMemo(
        () => ({
            // Info Section
            customer_name: device.customer_name ?? '',
            name: device.name ?? '',
            address: device.address ?? '',
            delivery_type: device.delivery_type ?? '',
            group: device.group ?? '',
            billing: {
                pricePerGallon: device.billing?.pricePerGallon,
                taxPercentage: device.billing?.taxPercentage,
            },
            // Tank Section
            capacity: device.capacity,
            consumptionType: Object.entries(device.consumptionType ?? {})
                .filter((c) => c[1] === true)
                .map((c) => c[0]) ?? ['heating', 'hot_water'],
            orientation: device.orientation,
            delivery_instructions: device.delivery_instructions ?? '',
            gaugeType: device.gaugeType,
            fuelType: device.fuelType ?? '',

            // Notes section
            notes: device.notes ?? '',

            // Backoffice Sync Section
            accountId: device.accountId ?? '',
            backOfficeAccountNumber:
                getBackOfficeAccountNumber(device.accountId) ?? '',
            backOfficeTankNumber:
                getBackOfficeTankNumber(device.accountId) ?? '',

            // Admin Section
            equation_id: device.equation_id ?? '',
            deviceSupplierId: device.protected?.supplierId ?? '',
            deviceSupplierName: supplierName,
            status: device.status ?? '',
            bo_sync_config: device.bo_sync_config ?? false,
            dialType: device.dialType ?? 'unknown',
        }),
        [device, supplierName]
    );

    /** Calculate the diff between the initial defaults (including existing values on the device) and the current values on the form. */
    const getChangedFields = (
        deviceInput: DeviceConfigInput
    ): Partial<Device> => {
        const changesObject = getObjectDiff(
            defaultDeviceConfigValues,
            deviceInput
        );

        return changesObject;
    };

    const onSave = (deviceInputParam: DeviceConfigInput) => {
        setIsSaving(true);
        setDeviceInput(deviceInputParam);
        if (user?.protected?.supplierId?.toString() !== '0')
            finalizeSave(deviceInputParam);
        else setConfirmationOpen(true);
    };

    const finalizeSave = async (deviceInputParam?: DeviceConfigInput) => {
        const actualDeviceInput = deviceInputParam ?? deviceInput;
        if (!actualDeviceInput) return;

        const newSupplierId: string = actualDeviceInput.deviceSupplierId ?? '';
        // Remove the deviceSupplierId + deviceSupplierName values as those are not actually fields we want to write to the device object.
        delete actualDeviceInput.deviceSupplierId;
        delete actualDeviceInput.deviceSupplierName;

        const newStatus: string = actualDeviceInput.status ?? '';
        // Do not try to update the status using the updateDeviceQuery, we will get an error. Need to use separate network call.
        delete actualDeviceInput.status;

        const consumptionType = mapConsumptionTypes(actualDeviceInput);
        let updatedFields = getChangedFields(actualDeviceInput);
        if (updatedFields.consumptionType) {
            updatedFields.consumptionType = consumptionType;
        }

        if (updatedFields.address) {
            const { error, ...geometry } = await getGeocodeData(
                updatedFields.address
            );

            if (error) {
                dispatch(
                    activateSnackbar({ message: error, severity: 'error' })
                );

                return false;
            }

            updatedFields.geometry = geometry;
        } else if (updatedFields.address === '') {
            updatedFields.geometry = undefined;
        }

        // If optional fields were previously filled and then deleted by the user, we need to set that undefined field to null to have the data update properly.
        updatedFields = recursivelyNullifyUndefinedValues(updatedFields);

        if (device) {
            const oldSupplierId = device.protected?.supplierId;
            // Only special admin users can change supplierId so we have to do a special network call.
            if (newSupplierId && newSupplierId !== oldSupplierId) {
                // Removing the supplier updates the device history, removes the supplier, removes device users, and clears alerts, status, consumption data.
                await removeSupplierFromDeviceQuery({
                    deviceId: device.deviceId,
                });
                // Adds the new supplierId to the device.
                await updateDeviceSupplierIdQuery({
                    deviceId: device.deviceId,
                    newSupplierId,
                });
            }

            // Only special admin users can change device status so we have to do a special network call.
            // Do this after the removeSupplier call is made (if it is made at all) because that endpoint deletes status data.
            if (newStatus && newStatus !== device.status) {
                await updateDeviceStatusQuery({
                    deviceId: device.deviceId,
                    newStatus,
                });
            }

            // Finally, make patch request to update all other device fields, which are editable by regular users.
            updateDeviceQuery({
                deviceId: device.deviceId,
                updatedFields,
            }).then(({ data }) => {
                setIsSaving(false);
                dispatch(
                    activateSnackbar({
                        message: 'Saved Successfully',
                        severity: 'success',
                    })
                );
                if (data) setDevice(data);
            });
        }
    };

    const confirmationOkay = () => {
        setConfirmationOpen(false);
        finalizeSave();
    };

    const confirmationCancel = () => {
        setConfirmationOpen(false);
        setDeviceInput(null);
    };

    const mapConsumptionTypes = (
        device: DeviceConfigInput
    ): DeviceConsumptionType => {
        const consumptionType: any = {
            bulk_storage: false,
            commercial_industrial: false,
            cooking: false,
            fireplace: false,
            generator: false,
            heating: false,
            hot_water: false,
            laundry_dryer: false,
            pool: false,
            retail_fill_up: false,
            backup_heating: false,
        };

        for (const type of device.consumptionType) {
            consumptionType[type] = true;
        }

        return consumptionType;
    };

    const onError = (error: FieldValues) => {
        const messages = Object.keys(error).map((key) => error[key]?.message);
        dispatch(
            activateSnackbar({
                message: messages.join('\n'),
                severity: 'error',
            })
        );
    };

    return (
        <div data-testid="DeviceConfig" className={styles.DeviceConfig}>
            <FormProvider
                defaultValues={defaultDeviceConfigValues}
                validationSchema={deviceConfigSchema}
            >
                <div className={styles.cardRow}>
                    <div className={styles.cardCol}>
                        <DeviceConfigInfo
                            defaultValues={defaultDeviceConfigValues}
                        />

                        {supplierInfo && (
                            <DeviceConfigBackofficeSync
                                supplierInfo={supplierInfo}
                                device={device}
                                supplierId={viewAsSupplier}
                                setOpenBackofficeDeliveryDialog={
                                    setBackofficeDeliveryDialogOpen
                                }
                                onDeviceUpdate={setDevice}
                            />
                        )}
                    </div>

                    <div className={styles.cardCol}>
                        <DeviceConfigTank isVirtual={device.virtual} />

                        <DeviceConfigNotes />

                        {/* Only show admin section for the admin account. */}
                        {userProtected &&
                            supplierList &&
                            user?.protected?.supplierId?.toString() === '0' && (
                                <DeviceConfigAdmin
                                    userProtected={userProtected}
                                    device={device}
                                    suppliers={supplierList}
                                />
                            )}
                    </div>
                </div>

                <div className={styles.saveButtonContainer}>
                    <FormSubmitButton
                        onValidHandler={(deviceInput: DeviceConfigInput) => {
                            onSave(deviceInput);
                        }}
                        onErrorHandler={onError}
                        buttonLabel="Save"
                        className={styles.submitButton}
                        size="large"
                        isLoading={isSaving}
                        startIcon={<Save />}
                    />
                </div>
            </FormProvider>
            <ConfirmationDialog
                open={confirmationOpen}
                onOkay={confirmationOkay}
                onCancel={confirmationCancel}
                text="Are you sure you want to update this device?"
            />

            {supplierInfo && (
                <BackofficeDeliveryModal
                    modalOpen={backofficeDeliveryDialogOpen}
                    setModalOpen={setBackofficeDeliveryDialogOpen}
                    supplierId={supplierInfo.supplierId}
                    tankNumber={tankNumber}
                    device={device}
                    onDeviceUpdate={setDevice}
                />
            )}
        </div>
    );
};
